import React, { useRef, useCallback } from 'react';
import { Button, type ButtonProps } from '@mui/material';
import { gsap } from 'gsap';

export interface ScaleButtonProps extends Omit<ButtonProps, 'variant'> {
  /** MUI button variant */
  muiVariant?: ButtonProps['variant'];
  /** Icon element */
  icon?: React.ReactNode;
  /** Icon position */
  iconPosition?: 'start' | 'end';
  /** Children/button content */
  children: React.ReactNode;
}

/**
 * ScaleButton - Simple scale animation on hover/click
 * 
 * Atomic button component with subtle scale animation.
 * 
 * @example
 * ```tsx
 * <ScaleButton icon={<Icon />} iconPosition="start">
 *   Click me
 * </ScaleButton>
 * ```
 */
export function ScaleButton({
  muiVariant = 'contained',
  icon,
  iconPosition = 'start',
  children,
  onClick,
  ...props
}: ScaleButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1.05,
        duration: 0.3,
        ease: 'back.out(1.7)',
      });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }, []);

  const iconElement = icon ? (
    <span 
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center',
      }}
    >
      {icon}
    </span>
  ) : null;

  return (
    <Button
      ref={buttonRef}
      variant={muiVariant}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      {...props}
      sx={{
        willChange: 'transform',
        ...props.sx,
      }}
    >
      {iconPosition === 'start' && iconElement}
      {children}
      {iconPosition === 'end' && iconElement}
    </Button>
  );
}

export default ScaleButton;
